import { all, call, put, takeEvery } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { Document, FileType } from '../../types/common.types';
import { SagaPayload } from '../api.types';
import {
  CancelVideoUploadRequest,
  DownloadDocumentRequest,
  DownloadDocumentResponse,
  UploadChunkResponse,
  UploadDocumentsRequest
} from './document.types';

import {
  CANCELLING_VIDEO_UPLOAD,
  DOWNLOADING_DOCUMENT,
  UPLOADING_DOCUMENT,
  UPLOADING_DOCUMENTS
} from './document.constants';

import {
  requestCancelVideoUpload,
  requestDownloadDocument,
  requestUploadDocument,
  requestUploadDocuments
} from './document.actions';

import {
  getCancelVideoUploadRequest,
  getDownloadDocumentRequest,
  getUploadChunkRequest,
  getUploadDocumentRequest
} from './document.endpoints';

import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR } from '../../utils/constants';
import { isVideo } from '../../utils/util';

const CHUNK_SIZE = 1024 * 1024 * 5;

export function* performDownloadDocumentRequest({ payload }: SagaPayload<DownloadDocumentRequest, ArrayBuffer>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DOWNLOADING_DOCUMENT));

    const [endpoint, options] = getDownloadDocumentRequest(requestData, 'arraybuffer');

    const { data }: AxiosResponse<DownloadDocumentResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DOWNLOADING_DOCUMENT));
  }
}

export function* watchForDownloadDocumentRequest() {
  yield takeEvery(requestDownloadDocument.type, performDownloadDocumentRequest);
}

export function* performUploadDocumentRequest({ payload }: SagaPayload<Document, string>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPLOADING_DOCUMENT));

    const [endpoint, options] = getUploadDocumentRequest(requestData, {
      'Content-Type': 'application/x-binary',
      'Content-Length': requestData.size!.toString(),
      documentName: requestData.name
    });

    const { headers }: AxiosResponse = yield call(axios, endpoint, options);

    if (onSuccess && headers.documentexternalreference) {
      yield call(onSuccess, headers.documentexternalreference);
    }

    yield put(removeSpinner(UPLOADING_DOCUMENT));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(UPLOADING_DOCUMENT));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUploadDocumentRequest() {
  yield takeEvery(requestUploadDocument.type, performUploadDocumentRequest);
}

export function* performUploadDocumentsRequest({ payload }: SagaPayload<UploadDocumentsRequest, FileType[]>) {
  const { requestData, onSuccess } = payload;
  const uploadedDocuments = [];

  try {
    yield put(addSpinner(UPLOADING_DOCUMENTS));

    for (const document of requestData.documents) {
      if (isVideo(document.name)) {
        const totalChunks = Math.ceil(document.size / CHUNK_SIZE);
        let currentChunk = 0;

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, document.size);
          const chunk = document.slice(start, end);

          let retries = 0;
          let chunkUploaded = false;

          while (retries < 3 && !chunkUploaded) {
            try {
              const formData = new FormData();
              formData.append('chunk', chunk, `${document.name}.chunk${currentChunk}`);
              formData.append('fileName', document.name);
              formData.append('chunkIndex', currentChunk.toString());
              formData.append('totalChunks', totalChunks.toString());

              const [endpoint, options] = getUploadChunkRequest(formData, {
                'Content-Type': 'multipart/form-data; boundary=!@#'
              });

              const { data }: AxiosResponse<UploadChunkResponse> = yield call(axios, endpoint, options);

              if (data.objectKey) {
                uploadedDocuments.push({
                  name: document.name,
                  link: data.objectKey,
                  documentType: 'video'
                });
              }

              currentChunk++;
              chunkUploaded = true;
            } catch (_error) {
              retries++;
            }
          }

          if (!chunkUploaded) {
            yield put(requestCancelVideoUpload({ requestData: { fileName: document.name } }));
            throw new Error('Failed to upload video');
          }
        }
      } else {
        const [endpoint, options] = getUploadDocumentRequest(document, {
          'Content-Type': 'application/x-binary',
          'Content-Length': document.size!.toString(),
          documentName: document.name
        });

        const { headers }: AxiosResponse = yield call(axios, endpoint, options);

        uploadedDocuments.push({
          name: document.name,
          documentExternalReference: headers.documentexternalreference
        });
      }
    }

    if (onSuccess) {
      yield call(onSuccess, uploadedDocuments);
    }

    yield put(removeSpinner(UPLOADING_DOCUMENTS));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(UPLOADING_DOCUMENTS));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUploadDocumentsRequest() {
  yield takeEvery(requestUploadDocuments.type, performUploadDocumentsRequest);
}

export function* performCancelVideoUploadRequest({ payload }: SagaPayload<CancelVideoUploadRequest>) {
  try {
    yield put(addSpinner(CANCELLING_VIDEO_UPLOAD));

    const [endpoint, options] = getCancelVideoUploadRequest({
      fileName: payload.requestData.fileName
    });

    yield call(axios, endpoint, options);
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CANCELLING_VIDEO_UPLOAD));
  }
}

export function* watchForCancelVideoUploadRequest() {
  yield takeEvery(requestCancelVideoUpload.type, performCancelVideoUploadRequest);
}

export default function* documentSaga() {
  yield all([
    watchForDownloadDocumentRequest(),
    watchForUploadDocumentRequest(),
    watchForUploadDocumentsRequest(),
    watchForCancelVideoUploadRequest()
  ]);
}
